const EMBED_HOST = 'embed.gethomecrowd.com';
const CURRENT_DOCS_SCHEMA_VERSION = 5;

export const defaultEmbeddingDocsDocument = {
  schemaVersion: CURRENT_DOCS_SCHEMA_VERSION,
  sidebarEyebrow: 'Embedding',
  sidebarTitle: 'Homecrowd WebView Guide',
  sidebarCopy: 'Plug the Homecrowd experience into any iOS, Android, React Native, or Flutter app with a single URL.',
  topics: [
    {
      slug: 'overview',
      label: 'Overview',
      title: 'Embed Homecrowd with one WebView URL',
      intro:
        'Homecrowd embed is a hosted web experience for rewards, cards, offers, and account flows. Your backend requests a short-lived autologin token with your school API key, then your app opens a WebView URL with the school context and token.',
      subtopics: [
        {
          id: 'what-you-load',
          label: 'What you load',
          blocks: [
            {
              type: 'paragraph',
              text: 'Load `https://embed.gethomecrowd.com/` inside your app. Homecrowd handles the UI, session bootstrap, and navigation inside the embedded experience.',
            },
          ],
        },
        {
          id: 'required-inputs',
          label: 'What you provide',
          blocks: [
            {
              type: 'list',
              items: [
                'Use the static `schoolId` provided by Homecrowd for school context and branding.',
                'Request a short-lived `token` from your backend before opening the WebView.',
                'Pass `view` only when you want to open a specific first screen.',
                'Pass `wildfireAppId` when your client uses a dedicated Wildfire Application ID.',
                'Keep the `school API key` on your backend only. Never include it in a mobile app or frontend bundle.',
              ],
            },
          ],
        },
        {
          id: 'entry-point',
          label: 'Production entry point',
          blocks: [
            {
              type: 'callout',
              title: 'Embed URL',
              text: '`https://embed.gethomecrowd.com/`',
            },
          ],
        },
      ],
    },
    {
      slug: 'quick-start',
      label: 'Quick Start',
      title: 'Open the embed with schoolId and a backend-issued token',
      intro:
        'Homecrowd provides your static school ID and school API key. Your backend uses those values to request a short-lived autologin token, then your app opens the embed URL with `schoolId`, `token`, optional `view`, and optional `wildfireAppId`.',
      subtopics: [
        {
          id: 'example-url',
          label: 'Example URL',
          blocks: [
            {
              type: 'code',
              label: 'Example URL',
              code: 'https://embed.gethomecrowd.com/?schoolId=YOUR_SCHOOL_ID&token=autologin%3A...&view=home&wildfireAppId=YOUR_WILDFIRE_APP_ID',
            },
          ],
        },
        {
          id: 'url-parameters',
          label: 'URL parameters',
          blocks: [
            {
              type: 'table',
              columns: ['Parameter', 'Required', 'Description', 'Example'],
              rows: [
                ['`schoolId`', 'Yes', 'Static Homecrowd school identifier provided with your integration credentials', '`YOUR_SCHOOL_ID`'],
                ['`token`', 'Yes', 'Short-lived autologin token returned by the school autologin endpoint', '`autologin:...`'],
                ['`view`', 'No', 'Initial screen to open. Available screens include `home`, `rewards`, `offers`, `cards`, and `content`. Defaults to `home` when omitted.', '`home`'],
                ['`wildfireAppId`', 'Recommended', 'Client-specific Wildfire Application ID used for Wildfire offers/track-click routing', '`322`'],
              ],
            },
            {
              type: 'callout',
              title: 'Where to get your wildfireAppId',
              text: 'Contact support@gethomecrowd.com to get your client-specific `wildfireAppId`.',
            },
          ],
        },
        {
          id: 'get-school-id',
          label: 'Where to get schoolId and API key',
          blocks: [
            {
              type: 'paragraph',
              text: 'Homecrowd provides your static `schoolId` and `school API key` during setup. If your team has access to the Homecrowd school admin portal, the Settings page shows a copyable `schoolId` and lets authorized school staff create or regenerate the API key.',
            },
            {
              type: 'callout',
              title: 'Store these values differently',
              text: '`schoolId` is a static identifier and can be kept as configuration. `school API key` is a server-side secret and must only be stored on your backend.',
            },
            {
              type: 'callout',
              title: 'Do not discover schoolId at runtime',
              text: 'Use the `schoolId` assigned to your integration. Do not ask the mobile app to search public school lists before opening the embed.',
            },
          ],
        },
        {
          id: 'url-builder',
          label: 'JavaScript URL builder',
          blocks: [
            {
              type: 'code',
              label: 'JavaScript URL builder',
              code: `const params = new URLSearchParams({
  schoolId,
  token: embedToken,
  view: 'home',
  wildfireAppId: '322',
});

const embedUrl = \`https://embed.gethomecrowd.com/?\${params.toString()}\`;`,
            },
          ],
        },
      ],
    },
    {
      slug: 'auth-token',
      label: 'Auth Token',
      title: 'Create a short-lived autologin token',
      intro:
        'Generate the token on your server after your user is already authenticated in your app. Use the school API key and school ID provided by Homecrowd, send the verified user profile without a password, then pass the returned token into the embed URL.',
      subtopics: [
        {
          id: 'partner-seven-step-flow',
          label: 'Partner integration in 7 steps',
          blocks: [
            {
              type: 'list',
              items: [
                'User signs in to your app using your existing authentication.',
                'Your mobile app asks your backend to prepare a Homecrowd embed URL.',
                'Your backend calls `POST https://api.gethomecrowd.com/api/embed/v1/auth/school-autologin-token/` with `Authorization: Bearer YOUR_SCHOOL_API_KEY`.',
                'Your backend sends `schoolId`, `email`, `firstName`, and `lastName` in the JSON body. `externalUserId` is optional.',
                'Homecrowd finds or creates the user, attaches the school, and returns a short-lived `autologin:` token.',
                'Your backend returns an embed URL to the mobile app: `https://embed.gethomecrowd.com/?schoolId=YOUR_SCHOOL_ID&token=<urlencoded token>&view=home`. If `view` is omitted, Homecrowd opens `home` by default.',
                'Your mobile app opens that URL in a WebView. Do not open the embed without a valid token in this integration.',
              ],
            },
          ],
        },
        {
          id: 'token-flow',
          label: 'Token flow',
          blocks: [
            {
              type: 'list',
              items: [
                'Authenticate the user in your own app first.',
                'Your mobile app asks your backend to prepare a Homecrowd embed URL.',
                'Your backend calls `POST https://api.gethomecrowd.com/api/embed/v1/auth/school-autologin-token/` with `Authorization: Bearer YOUR_SCHOOL_API_KEY`.',
                'Include `schoolId`, `email`, `firstName`, and `lastName` in the request body. Do not send a password.',
                'Homecrowd finds or creates the user, attaches the school, and returns a short-lived `autologin:` token.',
                'Build the WebView URL with `schoolId`, `token`, and optional `view`.',
                'Open the WebView with that URL.',
              ],
            },
          ],
        },
        {
          id: 'fetch-token',
          label: 'Fetch a token',
          blocks: [
            {
              type: 'paragraph',
              text: 'This request must be made from your backend. Keep `YOUR_SCHOOL_API_KEY` in server-side secret storage only. The mobile app should never contain or call Homecrowd with the school API key.',
            },
            {
              type: 'code',
              label: 'Fetch a token',
              code: `const response = await fetch('https://api.gethomecrowd.com/api/embed/v1/auth/school-autologin-token/', {
  method: 'POST',
  headers: {
    Authorization: \`Bearer \${process.env.HOMECROWD_SCHOOL_API_KEY}\`,
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({
    externalUserId: currentUser.id,
    schoolId: process.env.HOMECROWD_SCHOOL_ID,
    email: currentUser.email,
    firstName: currentUser.firstName,
    lastName: currentUser.lastName,
  }),
});

const { token, expiresAt } = await response.json();`,
            },
            {
              type: 'code',
              label: 'Request body shape',
              code: `{
  "externalUserId": "partner-user-123",
  "schoolId": "YOUR_SCHOOL_ID",
  "email": "fan@example.com",
  "firstName": "John",
  "lastName": "Doe"
}`,
            },
          ],
        },
        {
          id: 'token-guidance',
          label: 'Implementation guidance',
          blocks: [
            {
              type: 'list',
              items: [
                'Create the embed autologin token server-side, not in the mobile client.',
                'Treat the returned `autologin:` token as short-lived and request a fresh one right before opening the WebView.',
                '`email`, `schoolId`, `firstName`, and `lastName` are required. `externalUserId` is optional. If `firstName` or `lastName` is missing or blank, the request is rejected.',
                'If the email already exists in Homecrowd, the existing account is used. If not, Homecrowd creates a passwordless embedded account.',
                'Do not unpack user fields into query params. Pass the token string as-is.',
                'Use only the school autologin endpoint for this integration.',
                'Do not generate the `autologin:` token yourself. Always request it from Homecrowd.',
                'If your backend cannot obtain a token, do not open the WebView. Show a native error and let the user retry.',
                'Use the `schoolId` query param for school context and branding instead of passing colors or API hosts.',
                'If the WebView URL length is a concern, keep `schoolId` and `view` in the URL and set `window.__HC_EMBED_HOST_CONFIG__` to `{ schoolId, token, view }` before the document loads (same object shape as `HomecrowdEmbed.configure`).',
              ],
            },
          ],
        },
      ],
    },
    {
      slug: 'webview',
      label: 'WebView Setup',
      title: 'Open the URL inside your app shell',
      intro:
        'Any WebView container works as long as JavaScript and DOM storage are enabled. The examples below show the same three parameters passed into different native shells.',
      subtopics: [
        {
          id: 'react-native',
          label: 'React Native',
          blocks: [
            {
              type: 'code',
              label: 'React Native',
              code: `import { WebView } from 'react-native-webview';

const params = new URLSearchParams({
  schoolId,
  token: embedToken,
  view: 'home',
  wildfireAppId,
});

const embedUrl = \`https://embed.gethomecrowd.com/?\${params.toString()}\`;

const hostConfigInjected = \`window.__HC_EMBED_HOST_CONFIG__=\${JSON.stringify({
  schoolId: String(schoolId),
  token: embedToken,
  view: 'home',
})};true;\`;

export function HomecrowdRewards() {
  return (
    <WebView
      source={{ uri: embedUrl }}
      javaScriptEnabled
      domStorageEnabled
    />
  );
}

export function HomecrowdRewardsLongToken() {
  const embedUrlNoToken = \`https://embed.gethomecrowd.com/?\${new URLSearchParams({
    schoolId,
    view: 'home',
  }).toString()}\`;

  return (
    <WebView
      source={{ uri: embedUrlNoToken }}
      injectedJavaScriptBeforeContentLoaded={hostConfigInjected}
      javaScriptEnabled
      domStorageEnabled
    />
  );
}`,
            },
          ],
        },
        {
          id: 'ios',
          label: 'iOS / WKWebView',
          blocks: [
            {
              type: 'code',
              label: 'iOS / WKWebView',
              code: `import WebKit

let params = "schoolId=\\(schoolId)&token=\\(embedToken)&view=home&wildfireAppId=\\(wildfireAppId)"
let url = URL(string: "https://embed.gethomecrowd.com/?\\(params)")!

let webView = WKWebView(frame: view.bounds)
webView.load(URLRequest(url: url))
view.addSubview(webView)`,
            },
          ],
        },
        {
          id: 'android',
          label: 'Android / WebView',
          blocks: [
            {
              type: 'code',
              label: 'Android / WebView',
              code: `import android.webkit.WebView

val params = "schoolId=$schoolId&token=$embedToken&view=home&wildfireAppId=$wildfireAppId"
val url = "https://embed.gethomecrowd.com/?$params"

val webView = WebView(this)
webView.settings.javaScriptEnabled = true
webView.settings.domStorageEnabled = true
webView.loadUrl(url)
setContentView(webView)`,
            },
          ],
        },
        {
          id: 'flutter',
          label: 'Flutter',
          blocks: [
            {
              type: 'code',
              label: 'Flutter',
              code: `import 'package:webview_flutter/webview_flutter.dart';

final params = 'schoolId=$schoolId&token=$embedToken&view=home&wildfireAppId=$wildfireAppId';

WebView(
  initialUrl: 'https://embed.gethomecrowd.com/?$params',
  javascriptMode: JavascriptMode.unrestricted,
)`,
            },
          ],
        },
      ],
    },
    {
      slug: 'events',
      label: 'Events',
      title: 'Listen for messages from the embed',
      intro:
        'The WebView posts structured events back to the host app so you can respond to readiness, authentication, navigation, card-linking, and failures.',
      subtopics: [
        {
          id: 'event-types',
          label: 'Event reference',
          blocks: [
            {
              type: 'table',
              columns: ['Event', 'Payload', 'Description', 'When to use it'],
              rows: [
                ['`homecrowd:ready`', '`—`', 'Embed finished loading', 'Enable host-side actions after this fires'],
                ['`homecrowd:login`', '`{ user }`', 'User authenticated inside the embed', 'Sync host-side state if needed; set Safari extension `USER_ID`'],
                ['`homecrowd:logout`', '`—`', 'User logged out', 'Clear any related state in the host app; clear extension `USER_ID`'],
                ['`homecrowd:route-change`', '`{ route }`', 'Embed navigated to a new screen', 'Track analytics or update host chrome'],
                ['`homecrowd:card-link`', '`{ type }`', 'Card-link flow started', 'React when you need to monitor linking state'],
                ['`homecrowd:open-url`', '`{ url }`', 'Embed requested an external URL', 'Open the URL in a browser or native in-app browser'],
                [
                  '`homecrowd:request-extension-status`',
                  '`—`',
                  'Embed asks whether the Safari extension has been detected on device',
                  'Reply with `homecrowd:extension-status` / `HomecrowdEmbed.setExtensionStatus({ enabled })`',
                ],
                ['`homecrowd:error`', '`{ message }`', 'Embed hit an error', 'Log or surface the failure in the host app'],
              ],
            },
            {
              type: 'callout',
              title: 'What `enabled` means (avoid confusion)',
              text: 'Settings → Safari → Extensions ON is required, but it is **not** what `enabled` reports. `SafariExtensionStatus.getSafariExtensionEnabled()` returns `true` only after the extension has pinged the host via the shared App Group (`EXTENSION_INSTALLED_OR_RAN` + `LAST_EXTENSION_PING_AT`). That ping runs when the extension’s native handler receives a message — typically after the user opens the extension popup once. So: toggle ON ≠ detected; detection = App Group ping after the extension has actually run. Until that happens, reply `{ enabled: false }`.',
            },
          ],
        },
        {
          id: 'event-react-native',
          label: 'Listening in React Native',
          blocks: [
            {
              type: 'code',
              label: 'Listening in React Native',
              code: `<WebView
  source={{ uri: embedUrl }}
  onMessage={(event) => {
    const data = JSON.parse(event.nativeEvent.data);
    if (data.type === 'homecrowd:login') {
      console.log('User logged in:', data.payload.user);
    }
  }}
/>`,
            },
          ],
        },
        {
          id: 'event-payload',
          label: 'Sample payload',
          blocks: [
            {
              type: 'code',
              label: 'Sample event payload',
              code: `{
  "type": "homecrowd:route-change",
  "payload": {
    "route": "/rewards"
  }
}`,
            },
          ],
        },
      ],
    },
    {
      slug: 'runtime',
      label: 'Runtime Control',
      title: 'Update the embed after it mounts',
      intro:
        'If your host app needs to refresh the session, switch schools, or move the user to another tab without rebuilding the whole WebView, send runtime commands into the embed.',
      subtopics: [
        {
          id: 'runtime-commands',
          label: 'Available commands',
          blocks: [
            {
              type: 'code',
              label: 'Browser-side commands',
              code: `HomecrowdEmbed.navigate('cards');

HomecrowdEmbed.configure({
  schoolId,
  token: embedToken,
  view: 'home',
  wildfireAppId: '322',
});`,
            },
          ],
        },
        {
          id: 'runtime-platform',
          label: 'Platform commands',
          blocks: [
            {
              type: 'paragraph',
              text: 'Use `configure` when you want to apply a new school context or token. Use `navigate` when you only want to move between embed screens.',
            },
            {
              type: 'code',
              label: 'Platform commands',
              code: `webView.evaluateJavaScript("HomecrowdEmbed.navigate('cards')")

webView.evaluateJavascript("HomecrowdEmbed.configure({ schoolId: '$schoolId', token: '$embedToken', view: 'home', wildfireAppId: '322' })", null)

webViewRef.current.injectJavaScript("HomecrowdEmbed.navigate('cards'); true;");`,
            },
          ],
        },
      ],
    },
    {
      slug: 'safari-ios-distributable',
      label: 'Safari (iOS) package',
      title: 'Install the Homecrowd Safari Web Extension in your iOS app',
      intro:
        "Use this checklist as your partner setup guide. It is written for teams integrating the Homecrowd Safari extension into their own iOS app and publishing through their own App Store listing. Keep `iOS (Extension)/Resources/` unchanged unless Homecrowd asks you to modify it.",
      subtopics: [
        {
          id: 'saf-pkg-download',
          label: 'Download',
          blocks: [
            {
              type: 'downloadBlock',
              title: 'Download Extension',
              buttonLabel: 'Download',
              href: '/downloads/homecrowd-safari-extension.zip',
              downloadName: 'homecrowd-safari-extension.zip',
              helperText:
                'The archive includes the `iOS (Extension)/` tree, the `withSafariExtension.js` plugin, and `PARTNER_SETUP.md`. Unzip, read the guide, and follow the steps in this page. If the download does not work, contact support@gethomecrowd.com.',
            },
          ],
        },
        {
          id: 'saf-pkg-useful-links',
          label: 'Useful links',
          blocks: [
            {
              type: 'linkList',
              items: [
                {
                  href: 'https://kb.wildfire-corp.com/article/l0ol-how-to-implement-the-safari-mobile-extension',
                  label: 'How to implement the Mobile Safari Extension (Wildfire guide)',
                  caption:
                    ' - Simple documentation with video on how to implement the Mobile Safari Extension',
                },
              ],
            },
          ],
        },
        {
          id: 'saf-pkg-0',
          label: 'Step 1. Copy only required files',
          blocks: [
            {
              type: 'table',
              columns: ['Path in the package', 'Need for App Store / EAS?'],
              rows: [
                [
                  '`iOS (Extension)/` (full tree)',
                  'Yes. Safari Web Extension: manifest, JS, icons, `SafariWebExtensionHandler.swift`, plist, entitlements.',
                ],
                [
                  '`withSafariExtension.js`',
                  'Yes. Expo config plugin; put at `plugins/withSafariExtension.js` in your app.',
                ],
                [
                  '`extensionLaunchpad.xcodeproj`, `iOS (App)/`, `macOS/…`',
                  'No for store builds. Optional Xcode demo only.',
                ],
              ],
            },
            {
              type: 'list',
              items: [
                "Copy `iOS (Extension)/` to a path in your repo, for example `extensions/safari/iOS (Extension)/` (you will point `iosExtensionSourcePath` to it).",
                'Copy `withSafariExtension.js` to `<project root>/plugins/withSafariExtension.js`.',
                'The long-form guide is included in the zip as `PARTNER_SETUP.md`.',
              ],
            },
          ],
        },
        {
          id: 'saf-pkg-1',
          label: 'Step 2. Find your iOS project name (`iosAppProjectName`)',
          blocks: [
            {
              type: 'list',
              items: [
                "Run `npx expo prebuild` at least once (or the command you use to generate `ios/`).",
                'Open `ios/` and find `ios/YourProjectName/` plus `YourProjectName.xcodeproj`.',
                "Set `iosAppProjectName` to that folder name (case must match).",
                "If the main Xcode target name differs from the folder name, set `iosAppTargetName` too. Do not edit the plugin file for the name; use `app.config.js`.",
              ],
            },
          ],
        },
        {
          id: 'saf-pkg-2',
          label: 'Step 3. Create Apple identifiers',
          blocks: [
            {
              type: 'list',
              items: [
                'Create an App Group, for example `group.ABCD1234.com.yourorg.shared` (use your team id and a unique suffix).',
                'Create a second bundle id for the Safari Web Extension (for example `com.yourorg.app.safariExtension`), separate from the main app.',
                'Enable App Groups on both the main app id and the extension id.',
                'You replace these identifiers in the Apple portal and in your config, not the strings inside `iOS (Extension)/Resources/`.',
              ],
            },
          ],
        },
        {
          id: 'saf-pkg-3',
          label: 'Step 4. Add plugin config in `app.config.js`',
          blocks: [
            {
              type: 'paragraph',
              text: 'Add one object to the `expo.plugins` list. Use paths relative to the mobile app root. Substitute your `YourProjectName`, app group, extension bundle id, and `targetName`.',
            },
            {
              type: 'code',
              label: 'Plugin example',
              code: `[
  './plugins/withSafariExtension',
  {
    iosAppProjectName: 'YourProjectName',
    iosAppTargetName: 'YourProjectName',
    iosExtensionSourcePath: 'extensions/safari/iOS (Extension)',
    iosExtensionTargetPath: 'ios/SafariExtension',
    targetName: 'MyPartnerSafariExtension',
    bundleId: 'com.yourorg.app.safariExtension',
    appGroup: 'group.ABCD1234.com.yourorg.shared',
  },
],`,
            },
            {
              type: 'table',
              columns: ['Option', 'Meaning'],
              rows: [
                ['`iosAppProjectName`', '`ios/<Name>/` and `ios/<Name>.xcodeproj` from prebuild.'],
                [
                  '`iosAppTargetName`',
                  "Only if the main target name does not match `iosAppProjectName` (defaults to the same as project name).",
                ],
                ['`iosExtensionSourcePath`', 'Where you copied `iOS (Extension)/`.'],
                ['`iosExtensionTargetPath`', 'Where the plugin writes the built extension, often `ios/SafariExtension`.'],
                ['`targetName`', 'Xcode `appex` target name.'],
                ['`bundleId`', 'The extension’s bundle id only, not the host app.'],
                ['`appGroup`', 'The App Group string from Apple Developer.'],
              ],
            },
            {
              type: 'paragraph',
              text: 'The main iOS app should include `com.apple.security.application-groups` with the same group (the plugin can merge it when `appGroup` is set; verify in generated entitlements).',
            },
            {
              type: 'callout',
              title: 'Client-specific values you must replace',
              text: 'Do not ship Homecrowd defaults. Replace extension identity/signing values in your config and Apple portal before build.',
            },
            {
              type: 'list',
              items: [
                'In plugin config replace: `iosExtensionSourcePath`, `iosExtensionTargetPath`, `targetName`, `bundleId`, and `appGroup`.',
                'In `expo.extra.eas.build.experimental.ios.appExtensions` use the same `targetName`, the same extension `bundleIdentifier` as `bundleId`, and the same app group in entitlements.',
                'Set your host app bundle id in `expo.ios.bundleIdentifier` and create provisioning for both host app + extension bundle ids.',
                'Update extension branding in `iOS (Extension)/Info.plist` (`CFBundleDisplayName`, `CFBundleDescription`).',
                'Update copy/branding in `iOS (Extension)/Resources/_locales/en/messages.json` (Homecrowd strings).',
                'If you keep the optional onboarding/demo UI, replace demo links/placeholders in `iOS (App)/Resources/Main.js` (`wildlink`, `example.com`, `MSE-Template-User-ID`).',
              ],
            },
            {
              type: 'callout',
              title: 'Generated automatically by the plugin',
              text: 'Do not manually patch generated files in `ios/` after prebuild. The plugin writes and wires these for you.',
            },
            {
              type: 'list',
              items: [
                'App Group replacement inside `SafariWebExtensionHandler.swift` via `__HOMECROWD_APP_GROUP__`.',
                'Generated bridge files in the host app target: `ExtensionConfigModule.swift/.m` and `SafariExtensionStatus.swift/.m`.',
                'Generated `SafariExtension.entitlements` and Xcode target/build settings linkage for the extension.',
              ],
            },
          ],
        },
        {
          id: 'saf-pkg-4',
          label: 'Step 5. Configure EAS app extension signing',
          blocks: [
            {
              type: 'list',
              items: [
                "Under `expo.extra.eas.build.experimental.ios.appExtensions` add an entry: same `targetName` as the plugin, same `bundleIdentifier` as the extension `bundleId`, and `entitlements` with `com.apple.security.application-groups` set to the same `appGroup`.",
                "Without that, the `appex` may not sign or embed in the IPA.",
              ],
            },
          ],
        },
        {
          id: 'saf-pkg-5-embed-bridge',
          label: 'Step 6. Wire embed login to extension user id',
          blocks: [
            {
              type: 'paragraph',
              text: 'Homecrowd embed already emits `homecrowd:login` and `homecrowd:logout` to the native host app. Use those events to sync `user.id` into Safari extension shared storage through `ExtensionConfigModule`. Embed also asks the host whether the Safari extension has been detected (`homecrowd:request-extension-status`); reply with `homecrowd:extension-status` so embed can PATCH `is_extension_enabled` for the logged-in user. Detection is an App Group ping after the extension runs (usually open popup once) — not the Safari Settings toggle alone.',
            },
            {
              type: 'callout',
              title: 'Required bridge behavior',
              text: 'On `homecrowd:login` set extension `USER_ID` and enable flags. On `homecrowd:logout` clear `USER_ID` and disable flags. On `homecrowd:request-extension-status` reply with detected Safari extension status via `homecrowd:extension-status` (or `HomecrowdEmbed.setExtensionStatus`).',
            },
            {
              type: 'callout',
              title: 'End-user steps for `enabled: true`',
              text: '1) Install/update the app that embeds the Safari Web Extension. 2) Enable it in **Settings → Safari → Extensions → [Your brand]**. 3) Open Safari and open the extension popup once so `nativePing.js` can message the native handler and write the App Group ping. Only after step 3 will `getSafariExtensionEnabled()` return `true`. Do not treat the Safari Settings toggle alone as proof the host should send `{ enabled: true }`.',
            },
            {
              type: 'code',
              label: 'React Native WebView host example',
              code: `import React, { useCallback, useRef } from 'react';
import { NativeModules } from 'react-native';
import { WebView } from 'react-native-webview';

const { ExtensionConfigModule, SafariExtensionStatus } = NativeModules;

export default function HomecrowdEmbedScreen({ embedUrl }) {
  const webRef = useRef(null);

  const replyExtensionStatus = useCallback(async () => {
    let enabled = false;
    try {
      if (SafariExtensionStatus?.getSafariExtensionEnabled) {
        enabled = !!(await SafariExtensionStatus.getSafariExtensionEnabled());
      }
    } catch (_) {}
    const js =
      'window.HomecrowdEmbed && HomecrowdEmbed.setExtensionStatus(' +
      JSON.stringify({ enabled }) +
      ')';
    webRef.current?.injectJavaScript(js + '; true;');
  }, []);

  const onMessage = useCallback((event) => {
    let msg = null;
    try {
      msg = JSON.parse(event.nativeEvent.data || '{}');
    } catch {
      return;
    }

    const type = msg?.type;
    const payload = msg?.payload || {};

    if (type === 'homecrowd:login') {
      if (!ExtensionConfigModule) return;
      const userId = payload?.user?.id != null ? String(payload.user.id) : '';
      ExtensionConfigModule.setUserId(userId);
      if (ExtensionConfigModule.setCouponAndSplitFlags) {
        ExtensionConfigModule.setCouponAndSplitFlags(true, 1);
      } else {
        ExtensionConfigModule.setFlags(true, true, 1);
      }
      replyExtensionStatus();
      return;
    }

    if (type === 'homecrowd:logout') {
      if (!ExtensionConfigModule) return;
      ExtensionConfigModule.clearUserId();
      ExtensionConfigModule.setFlags(false, false, 0);
      return;
    }

    if (type === 'homecrowd:request-extension-status') {
      replyExtensionStatus();
      return;
    }
  }, [replyExtensionStatus]);

  return (
    <WebView
      ref={webRef}
      source={{ uri: embedUrl }}
      onMessage={onMessage}
    />
  );
}`,
            },
            {
              type: 'list',
              items: [
                'Do not pass `userId` manually in `HomecrowdEmbed.configure(...)`.',
                'Continue to call `configure({ schoolId, token, view, wildfireAppId })`; embed sends login/logout events when auth state changes.',
                'Validate `payload.user.id` before writing to extension storage.',
                'Use the same `appGroup` in host app and extension or Safari extension will not read `USER_ID`.',
                'Reply to `homecrowd:request-extension-status` with `{ enabled: true|false }` so embed can set `is_extension_enabled` in Homecrowd for the current user.',
                'You can also push status proactively after login with `HomecrowdEmbed.setExtensionStatus({ enabled })`.',
                '`enabled` here means “extension has pinged App Group at least once”, not merely “Safari Settings toggle is ON”.',
              ],
            },
          ],
        },
        {
          id: 'saf-pkg-5-non-expo',
          label: 'Step 6 (Non-Expo alternative). Configure extension directly in Xcode',
          blocks: [
            {
              type: 'paragraph',
              text: 'If your app is not using Expo/EAS, complete setup directly in Xcode.',
            },
            {
              type: 'list',
              items: [
                'Open your iOS app project in Xcode and add a new target: Safari Web Extension (`.appex`).',
                'Copy files from `iOS (Extension)/` into the new extension target (Info.plist, entitlements, `SafariWebExtensionHandler.swift`, and `Resources/`).',
                'Set the extension bundle identifier to your extension id (for example `com.yourorg.app.safariExtension`).',
                'Enable the same App Group in both targets (main app and extension): `Signing & Capabilities` -> `App Groups`.',
                'In the extension target, set `CODE_SIGN_ENTITLEMENTS` to the extension entitlements file and confirm it contains the same app group.',
                'In the main app target, verify `Embed App Extensions` includes your Safari extension `.appex`.',
                'Add a native bridge that writes Homecrowd `user.id` and flags to shared app-group `UserDefaults` (same behavior as `ExtensionConfigModule`).',
              ],
            },
            {
              type: 'callout',
              title: 'Non-Expo parity rule',
              text: 'Non-Expo integration must preserve the same runtime behavior as Expo: user id and extension flags are written to shared App Group storage, and extension + host app use the same group string.',
            },
          ],
        },
        {
          id: 'saf-pkg-6',
          label: 'Step 7. Build and release app update',
          blocks: [
            {
              type: 'list',
              items: [
                'Re-run prebuild after any plugin/path changes.',
                'Build an IPA with EAS or Xcode and confirm the extension `appex` is embedded.',
                'Ship this app update to users (TestFlight or App Store).',
                'After update, users must: (1) enable the extension in iOS Settings → Safari → Extensions → [Your brand], then (2) open the extension popup in Safari once so the host can detect it via App Group ping.',
                'If the toggle is ON but popup was never opened, the host will still correctly reply `{ enabled: false }` until the first ping.',
              ],
            },
          ],
        },
        {
          id: 'saf-pkg-7',
          label: 'Step 8. Go-live checklist and common errors',
          blocks: [
            {
              type: 'list',
              items: [
                '`iOS (Extension)/` exists at the path in `iosExtensionSourcePath`.',
                "`withSafariExtension.js` is in `plugins/` and the plugin is listed in `expo.plugins`.",
                "`targetName`, extension `bundleId`, and `appGroup` are identical across plugin config, EAS `appExtensions`, and Apple Developer.",
                "Host app bundle id, extension bundle id, and provisioning profiles are created for the same Apple team.",
                'You are not relying on fallback defaults like `group.RW7XQVY7CD.homecrowd.extension`.',
                "Brand strings in `Info.plist` + `_locales/en/messages.json` were replaced for your client brand.",
                "After sign-in, your app calls `ExtensionConfigModule` and replies to `homecrowd:request-extension-status` (see Step 6 and Homecrowd `App.js`).",
              ],
            },
            {
              type: 'table',
              columns: ['Symptom', 'What to check'],
              rows: [
                [
                  "Error: Safari Extension source path does not exist",
                  "`iosExtensionSourcePath` or a missing `iOS (Extension)/` copy.",
                ],
                [
                  "Bridge or target issues",
                  "Mismatched target naming (`targetName`, Xcode target, or host target) or missing prebuild regeneration.",
                ],
                [
                  'Extension still uses Homecrowd identity/branding',
                  'Replace `bundleId`, `appGroup`, Info.plist display/description, and `_locales/en/messages.json` texts.',
                ],
                ['Extension signing errors', "App group, EAS `appExtensions`, provisioning for both app and extension ids, and same Apple team across all identifiers."],
              ],
            },
          ],
        },
      ],
    },
  ],
};

export function cloneEmbeddingDocsDocument(document = defaultEmbeddingDocsDocument) {
  return JSON.parse(JSON.stringify(document));
}

function replaceEmbedHost(value) {
  if (typeof value === 'string') {
    return value.split('app.gethomecrowd.com').join(EMBED_HOST);
  }

  if (Array.isArray(value)) {
    return value.map(replaceEmbedHost);
  }

  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, nestedValue]) => [key, replaceEmbedHost(nestedValue)])
    );
  }

  return value;
}

export function normalizeEmbeddingDocsDocument(document = defaultEmbeddingDocsDocument) {
  const normalizedDocument = replaceEmbedHost(cloneEmbeddingDocsDocument(document));
  if (!normalizedDocument || normalizedDocument.schemaVersion !== CURRENT_DOCS_SCHEMA_VERSION) {
    return cloneEmbeddingDocsDocument(defaultEmbeddingDocsDocument);
  }
  return normalizedDocument;
}
